import { IComponentOptions, IScope, ITimeoutService } from "angular";
import colors from "wwwroot/json/stats-colors.json";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { IEnrichedEmployee } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ChartDrawData, Chartist, IChartistSeriesData, ILineChartOptions } from "@gtmhub/shared/libs/chartist/chartist.module";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { IEnrichedTeam } from "@gtmhub/teams";
import { getColorForIndex, roundFloatAtMost2DecPlaces } from "@gtmhub/util";
import { assigneeFromRedux } from "@webapp/assignees/utils/assignee.utils";
import { IGoalAttainmentOverTimeStats, IProfileQueryParams, ITeamAvgProgressStats, IUserAvgProgressStats } from "../../models/models";
import { ProfileService } from "../../services/profileService";
import { IGenericCardStats } from "../generic-card/generic-card.component";
import { IGenericListCardStats, setListItemColor } from "../generic-list-card/generic-list-card";
import { ProfileFilterChangedEventArgs, ProfileSessionFilterEvents } from "../profile-session-filter/profile-session-filter.component";

interface IUserProgressStatsUI {
  goalAttainmentStats: IGenericCardStats;
  metricAttainmentStats: IGenericCardStats;
  goalAttainmentStatsOverTime: IGoalAttainmentOverTimeStats[];
}

interface ITeamProgressStatsUI {
  progressBySubTeam: IGenericListCardStats;
  teamProgress: IGenericListCardStats;
  goalAttainmentStatsOverTime: IGoalAttainmentOverTimeStats[];
}

interface IProgressSectionBindings {
  target: IEnrichedEmployee | IEnrichedTeam;
  indicators: {
    loading: boolean;
  };
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ProgressSectionCtrl extends IProgressSectionBindings {}
export class ProgressSectionCtrl extends GtmhubController {
  progressStats: IUserProgressStatsUI | ITeamProgressStatsUI;
  private sessionFilter: string;
  private viewExists: boolean;
  private profileSessionFilterEvents: ProfileSessionFilterEvents;

  static $inject = ["ProfileService", "UIErrorHandlingService", "$timeout", "$scope"];

  constructor(private profileService: ProfileService, private uiErrorHandlingService: UIErrorHandlingService, private $timeout: ITimeoutService, private $scope: IScope) {
    super();
  }

  $onInit(): void {
    this.viewExists = true;
    this.profileSessionFilterEvents = new ProfileSessionFilterEvents(this.$scope);
    this.profileSessionFilterEvents.onFilterChanged((args: ProfileFilterChangedEventArgs) => this.onFilterChanged(args.selectedFilter));
  }

  $onDestroy(): void {
    this.viewExists = false;
  }

  public onFilterChanged(filterOption: string): void {
    this.indicators.loading = true;
    this.sessionFilter = filterOption;
    this.getProgressStats();
  }

  public toggleLegendItemAndLineOpacity(index: number): void {
    const CLASS = index + 1;

    const lineSelector = `[class='ct-series ${CLASS}']`;

    const line = document.querySelectorAll<HTMLElement>(lineSelector)[0];

    const isLineVisible = window.getComputedStyle(line).getPropertyValue("opacity") === "1";
    line.style.opacity = isLineVisible ? "0.2" : "1";

    const legendItemSelector = `[class='section ${CLASS} ${isLineVisible ? "visible" : "blurred"}']`;
    const legendItem = document.querySelectorAll<HTMLElement>(legendItemSelector)[0];

    if (isLineVisible) {
      legendItem.classList.remove("visible");
      legendItem.classList.add("blurred");
    } else {
      legendItem.classList.remove("blurred");
      legendItem.classList.add("visible");
    }
  }

  private getProgressStats() {
    const queryParams: IProfileQueryParams = {
      sessionIds: this.sessionFilter,
    };

    this.profileService.getProgressStats(this.target.type, this.target.id, queryParams).then(
      (stats) => {
        if (this.target.type === "user") {
          this.progressStats = {
            goalAttainmentStats: this.formatAvgAttainmentStats(stats.goalAttainmentStats as IUserAvgProgressStats),
            metricAttainmentStats: this.formatAvgAttainmentStats(stats.metricAttainmentStats),
            goalAttainmentStatsOverTime: stats.goalAttainmentStatsOverTime.attainmentOverTime,
          };
        } else if (this.target.type === "team") {
          this.progressStats = {
            teamProgress: this.formatTeamProgress(stats.goalAttainmentStats as ITeamAvgProgressStats),
            progressBySubTeam: this.formatProgressBySubTeam(stats.goalAttainmentStats as ITeamAvgProgressStats),
            goalAttainmentStatsOverTime: stats.goalAttainmentStatsOverTime.attainmentOverTime,
          };
        }

        this.indicators.loading = false;

        // Added timeout to allow the DOM to render the chart (`.objective-progress-line-chart.${this.target.type}`) before we try to draw it
        window.setTimeoutOutsideAngular(() => {
          this.formatAttainmentOverTimeChart(stats.goalAttainmentStatsOverTime.attainmentOverTime);
        });
      },
      (error) => {
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private formatProgressBySubTeam = (stats: ITeamAvgProgressStats): IGenericListCardStats => {
    const state: IAssigneesStoreState = reduxStoreContainer.reduxStore.getState();

    const list = stats.subTeamAttainments.map((item, index) => {
      return {
        value: roundFloatAtMost2DecPlaces(item.avgAttainment * 100),
        progressBarColor: setListItemColor(index),
        assignee: assigneeFromRedux(state, item.team.id),
      };
    });

    return {
      main: { value: roundFloatAtMost2DecPlaces(stats.overallSubTeamsAvgAttainment * 100), suffix: "%" },
      company: { value: roundFloatAtMost2DecPlaces(stats.avgAccountAttainment * 100), suffix: "%" },
      list: list,
    };
  };

  private formatTeamProgress = (stats: ITeamAvgProgressStats): IGenericListCardStats => {
    const state: IAssigneesStoreState = reduxStoreContainer.reduxStore.getState();

    const list = stats.assigneeAttainments.map((item, index) => {
      return {
        value: roundFloatAtMost2DecPlaces(item.avgAttainment * 100),
        assignee: assigneeFromRedux(state, item.assignee.id),
        progressBarColor: setListItemColor(index),
      };
    });

    return {
      main: { value: roundFloatAtMost2DecPlaces(stats.avgTeamAttainment * 100), suffix: "%" },
      company: { value: roundFloatAtMost2DecPlaces(stats.avgAccountAttainment * 100), suffix: "%" },
      list: list,
    };
  };

  private formatAvgAttainmentStats = (stats: IUserAvgProgressStats): IGenericCardStats => {
    return {
      company: { value: roundFloatAtMost2DecPlaces(stats.avgAccountAttainment * 100), suffix: "%" },
      main: { value: roundFloatAtMost2DecPlaces(stats.avgUserAttainment * 100), suffix: "%" },
      team: { value: roundFloatAtMost2DecPlaces(stats.avgUserTeamsAttainment * 100), suffix: "%" },
    };
  };

  private formatAttainmentOverTimeChart = (stats: IGoalAttainmentOverTimeStats[]): void => {
    const chartDateLabels = stats[0]?.attainments.sort((a, b) => (a.lowerBound < b.lowerBound ? -1 : 1)).map((attainment) => attainment.label);

    const statsOverTimeUI = stats.reduce(
      (statsData, line, index) => {
        const { singleLineData, min, max } = line.attainments.reduce(
          (data, singleAttainment) => {
            const attainmentValue = roundFloatAtMost2DecPlaces(singleAttainment.attainment * 100);
            data.singleLineData.push({ y: attainmentValue });
            data.min = attainmentValue < data.min ? attainmentValue : data.min;
            data.max = attainmentValue > data.max ? attainmentValue : data.max;

            return data;
          },
          { singleLineData: [], min: statsData.min, max: statsData.max }
        );

        statsData.min = min;
        statsData.max = max;

        const series = {
          className: (index + 1).toString(),
          data: singleLineData,
        };

        statsData.series.push(series);
        return statsData;
      },
      { series: [], min: 0, max: 0 }
    );

    if (this.viewExists) {
      // we need to know if the view is destroyed otherwise execution continues,
      // chartist tries to find an element that no longer exists and type error occurs
      this.drawLineChart(chartDateLabels, statsOverTimeUI);
    }
  };

  private drawLineChart = (labels: string[], statsOverTime: { series: IChartistSeriesData[]; min: number; max: number }): void => {
    const values = {
      min: statsOverTime.min >= 0 ? 0 : Math.floor(statsOverTime.min / 5) * 5,
      max: statsOverTime.max <= 100 ? 100 : Math.floor(statsOverTime.max / 5) * 5,
    };
    const data = {
      labels,
      series: statsOverTime.series,
    };

    data.series.reverse();

    const options: ILineChartOptions = {
      fullWidth: true,
      chartPadding: { bottom: 30, left: 30, top: 30, right: 30 },
      width: "93%",
      height: "288px",
      axisX: {
        showGrid: false,
        labelOffset: {
          y: 6,
        },
        labelInterpolationFnc: function (value: string, index: number, arr: string[]) {
          const val = value.replace(/ /g, "<br/>");
          if (arr.length > 13) {
            if (arr.length % 2) {
              return index % 2 === 0 && val;
            } else {
              if (index === arr.length - 1 || (index % 2 === 0 && index !== arr.length - 2)) {
                return val;
              }
            }
          } else {
            return val;
          }
        },
      },
      axisY: {
        low: values.min,
        high: values.max,
        labelInterpolationFnc: function (value: number) {
          return value + "%";
        },
        referenceValue: 0,
        onlyInteger: true,
        scaleMinSpace: 25,
        labelOffset: {
          y: 2,
        },
      },
      showPoint: false,
      lineSmooth: true,
    };

    const chart = new Chartist.Line(`.objective-progress-line-chart.${this.target.type}`, data, options);

    chart.on("draw", (data: ChartDrawData) => {
      if (data.type === "label" && data.axis.counterUnits.pos === "y") {
        const textHtml = ["<p>", data.text, "</p>"].join("");

        const multilineText = new Chartist.Svg("svg").foreignObject(
          textHtml,
          {
            style: "overflow: visible;",
            x: data.x - 20,
            y: data.y,
            width: 45,
            height: data.height,
          },
          "ct-label"
        );
        data.element.replace(multilineText);
      }
    });

    this.$timeout(() => {
      statsOverTime.series.forEach((serie, index) => {
        const color = getColorForIndex(index, colors);

        document.querySelectorAll<HTMLElement>(".ct-series").forEach((el) => {
          if (el.classList.contains(`${index + 1}`)) {
            el.style.stroke = color;
          }
        });

        document.querySelectorAll<HTMLElement>(".section-color").forEach((el) => {
          if (el.classList.contains(`${index + 1}`)) {
            el.style.backgroundColor = color;
          }
        });
      });
    });
  };
}

export const ProgressSectionComponent: IComponentOptions = {
  bindings: {
    target: "<",
    indicators: "=",
  },
  controller: ProgressSectionCtrl,
  template: require("./progress-section.component.html"),
};
